import {
  ASSET_PLAN_TYPES,
  LOOSE_PLAN_TYPES,
  looseWorkType,
  parseIntervalInput,
  replacementQuantityDiffers,
  type LoosePlanType,
  type ObligationBasis,
  type PlanAssetDto,
  type PlanGroupDto,
  type PlanRowDto,
  type PlanType,
  type WorkType,
} from "@klantpunt/shared";
import { parseEuro } from "../../../pages/Platform";

/** Regel met zijn asset (null = losse regel) en groep. */
/** Per keer inclusief materialen (module Materialen); null = geen bedrag. */
export const perTime = (r: Pick<PlanRowDto, "costCents" | "materialsCents">) =>
  r.costCents == null && r.materialsCents == null ? null : (r.costCents ?? 0) + (r.materialsCents ?? 0);

export interface Ctx {
  r: PlanRowDto;
  a: PlanAssetDto | null;
  g: PlanGroupDto;
}

export const PLAN_WT: Record<PlanType, WorkType> = {
  vervanging: "replacement",
  herstel: "repair",
  onderhoud: "maintenance",
  overig: "maintenance",
  keuring: "inspection",
  inspectie: "inspection",
  controle: "inspection",
};

/** Bewerkbare kolommen van het werkblad. */
export type Field = "type" | "basis" | "start" | "end" | "interval" | "cost";
/** Velden in het paneel: de kolommen plus omschrijving en (bij posten) hoeveelheid. */
export type PanelField = Field | "name" | "qty";
export const FIELD_LABELS: Record<PanelField, string> = {
  name: "Omschrijving",
  qty: "Hoeveelheid",
  type: "Type",
  basis: "Eis",
  start: "Start",
  end: "Stopjaar",
  interval: "Interval",
  cost: "Per keer",
};

/**
 * Keuzes voor Type: bij een asset de keuringssoorten, plus vervanging zolang de asset die nog niet
 * heeft (en je MJOP mag bewerken); losse regels hun eigen lijst; vervanging bij een asset en
 * herstel liggen vast.
 */
export function typeOptions(x: Ctx, canEditItems: boolean): readonly PlanType[] | null {
  const { r, a } = x;
  if (r.source === "obligation") {
    const canReplace = a && canEditItems && !a.rows.some((o) => o.type === "vervanging");
    return canReplace ? ["vervanging", ...ASSET_PLAN_TYPES] : ASSET_PLAN_TYPES;
  }
  return a || r.type === "herstel" ? null : LOOSE_PLAN_TYPES;
}

export type Patch = Record<string, unknown>;
/** Resultaat van een invoer: wijziging voor de server, omzetten naar vervanging, of een foutmelding. */
export type Parsed = { patch: Patch } | { replace: true } | { error: string };

/** Invoer uit een cel of het paneel → wijziging voor PATCH /asset-obligations of /budget-items. */
export function parseField(r: PlanRowDto, key: PanelField, raw: string): Parsed {
  const ob = r.source === "obligation";
  const v = raw.trim();
  const year = /^\d{4}$/.test(v) ? Number(v) : null;
  switch (key) {
    case "name":
      if (!v) return { error: "Vul een omschrijving in" };
      return { patch: ob ? { name: v } : { description: v } };
    case "type":
      if (ob && v === "vervanging") return { replace: true };
      return { patch: ob ? { kind: v } : looseWorkType(v as LoosePlanType) };
    case "basis":
      return { patch: { basis: (v as ObligationBasis) || null } };
    case "start":
      if (ob)
        return !v || /^\d{4}-\d{2}-\d{2}$/.test(v)
          ? { patch: { nextDueOn: v || null } }
          : { error: "Datum als dd-mm-jjjj" };
      return year ? { patch: { firstYear: year } } : { error: "Startjaar als jjjj, bijvoorbeeld 2028" };
    case "end":
      if (v && !year) return { error: "Stopjaar als jjjj, of leeg voor doorlopend" };
      return { patch: ob ? { endYear: year } : { lastYear: year } };
    case "interval": {
      const iv = parseIntervalInput(v);
      if (!iv)
        return { error: ob ? "Interval als 1 wk, 2 wk, 6 mnd, 5 jr of eenmalig" : "Interval als 5 jr of eenmalig" };
      if (ob) return { patch: { intervalMonths: iv.months, intervalWeeks: iv.weeks } };
      if (iv.weeks || (iv.months && iv.months % 12))
        return {
          error:
            "Posten herhalen in hele jaren, bijvoorbeeld 5 jr. Wekelijks werk zet je als uitvoering bij een asset.",
        };
      return { patch: { cycleYears: iv.months ? iv.months / 12 : null } };
    }
    case "qty": {
      const q = Number(v.replace(/\./g, "").replace(",", "."));
      if (!v || !(q > 0)) return { error: "Hoeveelheid als getal groter dan 0, bijvoorbeeld 120 of 12,5" };
      return { patch: { quantity: q } };
    }
    case "cost": {
      const c = v ? parseEuro(v) : null;
      if (v && c === null) return { error: "Bedrag als 1.250 of 1.250,50" };
      if (ob) return { patch: { costCents: c } };
      if (c === null) return { error: "Een post heeft een bedrag nodig" };
      return { patch: { unitPriceCents: Math.round(c / (r.quantity || 1)) } };
    }
  }
}

/** Vervanging bij een asset met een eigen aantal dat afwijkt van de asset (docs/ASSETS-MJOP.md, Hoeveelheid). */
export const ownQuantity = ({ r, a }: Pick<Ctx, "r" | "a">) =>
  Boolean(
    a &&
    r.source === "item" &&
    r.type === "vervanging" &&
    r.quantity != null &&
    replacementQuantityDiffers(a, r.quantity, r.unit ?? "post"),
  );

/** Euro's zonder centen voor de planning ("1.250"). */
export const kEuro = (cents: number) => Math.round(cents / 100).toLocaleString("nl-NL");
